import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

export enum CacheTtlMs {
  FiveMinutes = 5 * 60 * 1000,
  OneDay = 24 * 60 * 60 * 1000,
}

interface CacheEntry<T> {
  value: Observable<T>;
  timer: ReturnType<typeof setTimeout>;
}

@Injectable({
  providedIn: 'root',
})
export class CachedDataService {
  private cache = new Map<string, CacheEntry<unknown>>();

  getOrSet<T>(key: string, factory: () => Observable<T>, ttlMs: number): Observable<T> {
    const existing = this.cache.get(key) as CacheEntry<T> | undefined;
    if (existing) return existing.value;

    const value = factory().pipe(shareReplay(1));
    const timer = setTimeout(() => {
      this.cache.delete(key);
    }, ttlMs);

    this.cache.set(key, { value, timer });
    return value;
  }

  clear(key?: string): void {
    if (!key) {
      this.cache.forEach((entry) => clearTimeout(entry.timer));
      this.cache.clear();
      return;
    }

    const entry = this.cache.get(key);
    if (entry) clearTimeout(entry.timer);
    this.cache.delete(key);
  }

  clearByPrefix(prefix: string): void {
    this.cache.forEach((entry, key) => {
      if (key.startsWith(prefix)) {
        clearTimeout(entry.timer);
        this.cache.delete(key);
      }
    });
  }
}
